import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, Typography, Container, Paper, CircularProgress, Snackbar, Alert, useTheme, useMediaQuery, keyframes, Grid } from "@mui/material";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthService } from "../services/authService.jsx";
import { useUserData } from "../services/userDataService.jsx"; // To potentially refetch data
import { PRICE_IDS, PLANS } from '../services/PriceIdService.jsx'; // Import pricing info
import airtableEmailService from '../services/airtableEmailService'; // Import Airtable email service
// tracking removed
import Header from '../components/Header';
import Footer from '../components/Footer';
import backdrop from "../img/backdrop.png"; // Reusing backdrop
import iosBadge from "../img/ios-badge.svg";
import googlePlayBadge from "../img/googleplay-badge.png";
import appHands from "../img/app-hands.png"; // Import the app-hands image
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import device1 from "../img/device-1.jpg";
import device2 from "../img/device-2.jpg";
import device3 from "../img/device-3.jpg";
import device4 from "../img/device-4.jpg";
import device5 from "../img/device-5.jpg";
import device6 from "../img/device-6.jpg";

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(1.05);
    filter: brightness(1.2);
  }
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
`;

// Helper for Google Ads Enhanced Conversions
async function sha256Lower(input) {
  if (!input) return '';
  const text = input.trim().toLowerCase();
  const enc = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// Tracking source helpers removed for GTM migration

function PaymentSuccess() {
  const { user, isLoading: isAuthLoading, isAuthenticated } = useAuthService();
  const { refetch: refetchUserData } = useUserData(); // Get refetch function
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const processedSessionRef = useRef(false); // Prevent double processing
  const [isLoadingPage, setIsLoadingPage] = useState(true); // Loading state for this page
  const [statusMessage, setStatusMessage] = useState('Processing your payment...'); // User-facing status
  const [isError, setIsError] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  // Dev preview mode: allows viewing UI without auth/Stripe when running locally
  const previewMode = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV &&
    (new URLSearchParams(location.search).get('preview') === '1' || new URLSearchParams(location.search).get('dev') === '1');

  useEffect(() => {
    // In preview mode, skip all backend/analytics work and just show the UI
    if (previewMode) {
      setIsLoadingPage(false);
      setIsError(false);
      setStatusMessage('Welcome to Screamify! Thank you for supporting independent horror.');
      return; // Do not run real processing
    }

    window.scrollTo(0, 0); // Scroll to top on mount

    const processPayment = async () => {
      const query = new URLSearchParams(location.search);
      const sessionId = query.get('session_id');

      // Wait for authentication check to complete unless we have a session_id for tracking
      if (isAuthLoading) {
        console.log("PaymentSuccess: Waiting for auth...");
        return; 
      }

      // If not authenticated AND no session, redirect to login; otherwise allow tracking to proceed
      if ((!isAuthenticated || !user?.userId) && !sessionId) {
        console.log("PaymentSuccess: User not authenticated and no session, redirecting to login.");
        navigate('/login?redirect=/paymentsuccess' + location.search);
        return;
      }

      // Only process if we have a session ID and haven't processed it yet
      if (sessionId && !processedSessionRef.current) {
        const userIdForLog = user?.userId || 'unknown';
        console.log(`PaymentSuccess: Processing subscription for user ${userIdForLog} with session ${sessionId}`);
        processedSessionRef.current = true; // Mark as processed
        setIsLoadingPage(true);
        setStatusMessage('Processing your payment...');
        setIsError(false);

        // Remove session_id from URL bar for cleanliness
        window.history.replaceState({}, document.title, location.pathname);

        try {
            // Stripe has already handled the subscription update
            // We just need to track conversions and update the UI
            console.log('PaymentSuccess: Payment processed successfully through Stripe');
            
            // Set success state
            setStatusMessage('Welcome to Screamify! Thank you for supporting independent horror.');
            setIsError(false);
            setSnackbarOpen(true); // Show success snackbar
            
            // === GTM: purchase event (with Enhanced Conversions email hash) ===
            window.dataLayer = window.dataLayer || [];

            // Keep hashed email for Google Ads Enhanced Conversions
            let hashedEmail = '';
            try {
              if (user?.attributes?.email) {
                hashedEmail = await sha256Lower(user.attributes.email);
              }
            } catch (e) {
              console.warn('EC hash failed; sending without email hash', e);
            }

            // CRITICAL FIX: Use the Stripe session_id directly as event_id
            // This MUST match what your Lambda sends to Meta CAPI
            const baseEvent = {
              event: 'evt_subscriptions_purchase',
              value: 2.99,
              currency: 'USD',
              plan_type: 'paid_monthly',
              event_id: sessionId  // USE SESSION_ID DIRECTLY - THIS IS THE FIX!
            };

            // Push once (include hashed email if present)
            window.dataLayer.push(
              hashedEmail ? { ...baseEvent, user_email_sha256: hashedEmail } : baseEvent
            );

            console.log('GTM push: evt_subscriptions_purchase with event_id:', sessionId);
            // === END GTM push ===

            // === GA4 direct tag load (ONLY on confirmed success) ===
            try {
              const GA_MEASUREMENT_ID = 'G-ZDBD03LPEX';
              const scriptId = `ga4-gtag-${GA_MEASUREMENT_ID}`;
              if (!document.getElementById(scriptId) && typeof window !== 'undefined') {
                const gaScript = document.createElement('script');
                gaScript.async = true;
                gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
                gaScript.id = scriptId;
                document.head.appendChild(gaScript);

                window.dataLayer = window.dataLayer || [];
                window.gtag = window.gtag || function(){ window.dataLayer.push(arguments); };
                window.gtag('js', new Date());
                window.gtag('config', GA_MEASUREMENT_ID);
                console.log('GA4 gtag initialized (PaymentSuccess only)');
              }
              // Send GA4 purchase event using the Stripe session_id as transaction_id
              if (typeof window !== 'undefined') {
                window.dataLayer = window.dataLayer || [];
                window.gtag = window.gtag || function(){ window.dataLayer.push(arguments); };
                window.gtag('event', 'purchase', {
                  transaction_id: sessionId,
                  value: 2.99,
                  currency: 'USD',
                  items: [
                    {
                      item_id: 'subscription_member',
                      item_name: 'Screamify Membership',
                      item_category: 'subscription',
                      price: 2.99,
                      quantity: 1,
                      plan_type: 'paid_monthly'
                    }
                  ]
                });
                console.log('GA4 purchase event sent', { transaction_id: sessionId });
              }
            } catch (e) {
              console.warn('GA4 gtag initialization failed (non-blocking):', e);
            }
            // === END GA4 direct tag ===
            
            // ===== AIRTABLE EMAIL CAPTURE FOR PAID SIGNUPS =====
            // Capture paid signup email to Airtable
            if (user?.attributes?.email) {
              const userEmail = user.attributes.email; // Get the actual email from attributes
              try {
                await airtableEmailService.capturePaidSignup(userEmail);
                console.log('PaymentSuccess: Paid signup email captured to Airtable:', userEmail);
              } catch (airtableError) {
                // Log error but don't block the user flow
                console.error('Failed to capture paid signup email to Airtable:', airtableError);
                // Could optionally track this error for monitoring
              }
            }
            // ===== END AIRTABLE EMAIL CAPTURE =====
            
            // Refetch user data from DynamoDB after a short delay
            if (refetchUserData) {
                setTimeout(() => {
                    refetchUserData();
                    console.log('PaymentSuccess: User data refreshed after subscription update');
                }, 1000); 
            }

        } catch (error) {
            console.error('PaymentSuccess: Error processing payment success:', error);
            // This is unlikely to happen since we're just tracking conversions
            setStatusMessage("Payment completed successfully! If you have any issues accessing content, please contact support.");
            setIsError(false); // Not really an error, payment was successful
            setSnackbarOpen(true); // Show info snackbar
        } finally {
            setIsLoadingPage(false);
        }
      } else if (!sessionId) {
          // Landed here without a session ID - maybe direct navigation?
          console.log("PaymentSuccess: No session_id found in URL.");
          setStatusMessage("Payment status unclear. If you just subscribed, your access should be active shortly.");
          setIsError(true); // Treat as an uncertain state
          setIsLoadingPage(false);
      } else {
           // Session already processed or auth still loading on subsequent renders
           // If loading finished and we already processed, keep success message
           if (!isLoadingPage) { 
              // Keep the final status message
           } else {
              // Still waiting for initial load/auth
              setIsLoadingPage(false); 
           }
      }
    };

    processPayment();

  }, [location, user, isAuthLoading, isAuthenticated, navigate, refetchUserData]);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>Payment Successful | Screamify™</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundImage: `url(${backdrop})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 0,
          }
        }}
      >
        <Header />
        <Container 
          component="main" 
          maxWidth="sm" 
          sx={{ 
            pt: { xs: 10, md: 16 }, 
            pb: 6, 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1
          }}
        >
          <Paper
            elevation={10}
            sx={{
              p: { xs: 3, sm: 5 },
              background: 'linear-gradient(135deg, rgba(25, 25, 25, 0.9), rgba(0, 0, 0, 0.9))',
              backdropFilter: 'blur(15px)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 0, 0, 0.3)',
              color: 'white',
              textAlign: 'left',
              boxShadow: '0 10px 30px rgba(255, 0, 0, 0.15), 0 0 10px rgba(255, 0, 0, 0.1)',
              animation: `${fadeIn} 0.8s ease-out`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(to right, #ff0000, #ff5500, #ff0000)',
                animation: `${pulse} 2s infinite ease-in-out`,
              }
            }}
          >
            {isLoadingPage ? (
                <Box sx={{ my: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CircularProgress 
                      sx={{ 
                        color: '#ff0000',
                        mb: 3,
                        animation: `${pulse} 2s infinite ease-in-out`,
                      }} 
                      size={60}
                    />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mt: 2, 
                        color: 'rgba(255, 255, 255, 0.9)',
                        animation: `${fadeIn} 1s ease-out`,
                      }}
                    >
                      {statusMessage}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ position: 'relative' }}>
                    {!isError && (
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <CheckCircleOutlineIcon 
                          sx={{ 
                            fontSize: { xs: 60, sm: 80 }, 
                            color: '#ff0000',
                            mt: 1,
                            animation: `${pulse} 2s infinite ease-in-out`,
                          }} 
                        />
                      </Box>
                    )}
                    <Typography 
                        variant={isMobile ? "h5" : "h4"} 
                        component="h1" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: isError ? '#ff9800' : '#ff0000',
                          animation: `${fadeIn} 0.5s ease-out`,
                          mb: 3,
                          textAlign: 'center'
                        }}
                    >
                        {isError ? 'Payment Status Update' : 'Payment Successful!'}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        mt: 2, 
                        mb: 4, 
                        fontSize: '1.1rem', 
                        whiteSpace: 'pre-line',
                        color: 'rgba(255, 255, 255, 0.9)',
                        animation: `${fadeIn} 0.7s ease-out`,
                        px: { xs: 1, sm: 3 },
                        textAlign: 'left'
                      }}
                    >
                        {statusMessage.replace('Screamify™', 'Screamify')}
                    </Typography>
                    
                    {!isError && (
                      <Box 
                        sx={{ 
                          mb: 4,
                          animation: `${fadeIn} 0.9s ease-out`,
                          background: 'rgba(0, 0, 0, 0.2)',
                          borderRadius: '8px',
                          p: 3,
                          mx: { xs: 0, sm: 2 },
                          textAlign: 'left'
                        }}
                      >
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 'medium', 
                            mb: 1.5, 
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '1rem'
                          }}
                        >
                          You're subscribed! Download the Screamify app to start streaming horror movies, originals, and podcasts.
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.75)',
                            mb: 3,
                            fontSize: '0.95rem'
                          }}
                        >
                          Screamify is available on iOS, Android mobile, Roku, FireTV, AppleTV, and Android TV.
                        </Typography>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 2,
                          mb: 3
                        }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box 
                              component="a"
                              href="https://apps.apple.com/us/app/screamify/id6738323226"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Box 
                                component="img"
                                src={iosBadge}
                                alt="Download on the App Store"
                                sx={{
                                  height: { xs: '36px', sm: '40px' },
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s, filter 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                    filter: 'brightness(1.1)'
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box 
                              component="a"
                              href="https://play.google.com/store/apps/details?id=com.screamify.app&pcampaignid=web_share"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Box 
                                component="img"
                                src={googlePlayBadge}
                                alt="Get it on Google Play"
                                sx={{
                                  height: { xs: '36px', sm: '40px' },
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s, filter 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                    filter: 'brightness(1.1)'
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                        
                        {/* Devices Gallery */}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, mb: 1, textAlign: 'center' }}>
                            Available on all your devices
                          </Typography>
                          <Grid container spacing={1}>
                            {[device1, device2, device3, device4, device5, device6].map((img, idx) => (
                              <Grid item xs={6} sm={4} key={idx}>
                                <Box
                                  component="img"
                                  src={img}
                                  alt={`Screamify devices ${idx + 1}`}
                                  loading="lazy"
                                  sx={{
                                    width: '100%',
                                    height: 'auto',
                                    borderRadius: 1,
                                    display: 'block',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                        
                        {/* App hands image */}
                        <Box 
                          sx={{ 
                            mt: 3,
                            display: 'flex',
                            justifyContent: 'center'
                          }}
                        >
                          <Box 
                            component="img"
                            src={appHands}
                            alt="Screamify app on mobile phone"
                            sx={{
                              maxWidth: '100%',
                              height: 'auto',
                              maxHeight: { xs: '220px', sm: '280px' },
                              animation: `${fadeIn} 1.2s ease-out`,
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    
                </Box>
            )}
          </Paper>
        </Container>
         <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
            <Alert onClose={handleCloseSnackbar} severity={isError ? 'warning' : 'success'} sx={{ width: '100%' }}>
              {statusMessage} 
            </Alert>
          </Snackbar>
        <Footer />
      </Box>
    </HelmetProvider>
  );
}

export default PaymentSuccess;